import {inject, Injectable} from '@angular/core';
import {PostLoginUser} from '../models/user/PostLoginUser';
import {Locale} from '../models/user/Locale';
import {SpringRestService} from '@aiknow-srl/aik-ngx-http';
import {environment} from '../../environments/environment';
import Bugsnag from "@bugsnag/js";
import {TranslateService} from "@ngx-translate/core";
import {PostLoginCB} from "../ope/user/PostLoginCB";
import {MenuGroup} from "../models/user/MenuGroup";
import {BaseTinyService} from "./BaseTinyService";
import {AWSConnectionOptions} from "../models/aws/AWSConnectionOptions";
import {S3ManagerService} from "./s3-manager.service";
import {DataType} from "../models/metric/DataType";
import {ApiConfig} from "../models/external-application/ApiConfig";

@Injectable({
  providedIn: 'root'
})
export class GlobalModelService extends BaseTinyService {

  public readonly STR_DIM_MAX = 255;
  public readonly STR_DIM_BIG = 100;
  public readonly STR_DIM_MID = 50;
  public readonly STR_DIM_MIN = 25;
  public readonly MAX_MODBUS_WRITE_PAUSE_MS: number = 30 * 1000;
  public readonly MIN_MODBUS_WRITE_PAUSE_MS: number = 0;
  public readonly DECIMAL_DIGITS_MIN = 0;
  public readonly DECIMAL_DIGITS_MAX = 10;

  /**
   * Array containing a list of default colors for the chart to init metrics
   * @protected
   */


  public readonly BASE_COLORS: string[] = [
    "#FFD200",
    "#FFAC01",
    "#F57801",
    "#E53D00",
    "#98CE00",
    "#62D600",
    "#4CB944",
    "#2B7C31",
    "#64CEA2",
    "#33A6AE",
    "#008BD1",
    "#33479B",
    "#FFB6C1",
    "#C60D77",
    "#A640EA",
    "#4112DB",
    "#C27630",
    "#8F450B",
    "#585858",
    "#000000",
  ];


  protected translate = inject(TranslateService);
  private be: SpringRestService = inject(SpringRestService);
  private s3: S3ManagerService = inject(S3ManagerService);

  // ----------------------------------
  //   POST LOGIN PARAMS
  // ----------------------------------

  constructor() {
    super();
    this.cleanupServiceFields();
  }

  // ----------------------------------
  /**
   * Application Id
   */
  private _appId!: string | null;

  // ***************************************
  /**
   * Getter for _appId
   */
  get appId(): string {
    // @ts-ignore
    return this._appId;
  }

  /**
   * Setter for _appId
   */
  set appId(value: string) {
    this._appId = value;
  }

  /**
   * Environment type
   */
  private _envId!: string | null;

  /**
   * Getter for _envId
   */
  get envId(): string {
    // @ts-ignore
    return this._envId;
  }

  /**
   * Setter for _envId
   */
  set envId(value: string) {
    this._envId = value;
  }

  /**
   * Application version
   */
  private _appVersion!: string | null;

  /**
   * Getter for _appVersion
   */
  get appVersion(): string {
    // @ts-ignore
    return this._appVersion;
  }

  /**
   * Setter for _appVersion
   */
  set appVersion(value: string) {
    this._appVersion = value;
  }

  /**
   * Environment Label type
   */
  private _envIdLabel!: string | null;

  /**
   * Getter for _envIdlabel
   */
  get envIdLabel(): string {
    // @ts-ignore
    return this._envIdLabel;
  }

  /**
   * Setter for _envIdlabel
   */
  set envIdLabel(value: string) {
    this._envIdLabel = value;
  }

  /**
   * Logged User
   */
  private _user!: PostLoginUser | null;

  /**
   * Getter for _user
   */
  get user(): PostLoginUser {

    // @ts-ignore
    return this._user;
  }

  /**
   * Setter for _user
   */
  set user(value: PostLoginUser) {
    this._user = value;
  }


  // ***************************************
  //      PostLogin Getters and Setters

  /**
   * Languages list
   */
  private _localeList!: Locale[];

  /**
   * Getter for _localeList
   */
  get localeList(): Locale[] {
    // @ts-ignore
    return this._localeList;
  }

  /**
   * Setter for _localeList
   */
  set localeList(value: Locale[]) {
    this._localeList = value;
  }

  /**
   * Roles list
   */
  private _rolesList!: string[];

  /**
   * Getter for _rolesList
   */
  get rolesList(): string[] {
    // @ts-ignore
    return this._rolesList;
  }

  /**
   * Setter for _rolesList
   */
  set rolesList(value: string[]) {
    this._rolesList = value;
  }

  /**
   * Modbus data type values
   */
  private _modbusDataTypeValues!: DataType[];

  get modbusDataTypeValues(): DataType[] {
    // @ts-ignore
    return this._modbusDataTypeValues;
  }

  set modbusDataTypeValues(value: DataType[]) {
    this._modbusDataTypeValues = value;
  }

  /**
   * S7 data type values
   */
  private _s7DataTypeValues!: DataType[];

  get s7DataTypeValues(): DataType[] {
    return this._s7DataTypeValues;
  }

  set s7DataTypeValues(value: DataType[]) {
    this._s7DataTypeValues = value;
  }

  /**
   * Modbus function values
   */
  private _modbusFunctionValues!: any[];

  get modbusFunctionValues(): any[] {
    // @ts-ignore
    return this._modbusFunctionValues;
  }

  set modbusFunctionValues(value: any[]) {
    this._modbusFunctionValues = value;
  }

  /**
   * Modbus function values
   */
  private _s7FunctionValues!: any[];

  get s7FunctionValues(): any[] {
    // @ts-ignore
    return this._s7FunctionValues;
  }

  set s7FunctionValues(value: any[]) {
    this._s7FunctionValues = value;
  }

  /**
   * Byte order values
   */
  private _byteOrderValues!: any[];

  get byteOrderValues(): any[] {
    // @ts-ignore
    return this._byteOrderValues;
  }

  set byteOrderValues(value: any[]) {
    this._byteOrderValues = value;
  }

  /**
   * Default byte order value
   */
  private _defaultByteOrderValue!: any;

  get defaultByteOrderValue(): any {
    // @ts-ignore
    return this._defaultByteOrderValue;
  }

  set defaultByteOrderValue(value: any) {
    this._defaultByteOrderValue = value;
  }

  /**
   * Modbus protocols values
   */
  private _protocolTypeValues!: any[];

  get protocolTypeValues(): any[] {
    // @ts-ignore
    return this._protocolTypeValues;
  }

  set protocolTypeValues(value: any[]) {
    this._protocolTypeValues = value;
  }

  /**
   * OS types
   */
  private _osTypesValues!: any[];

  get osTypesValues(): any[] {
    // @ts-ignore
    return this._osTypesValues;
  }

  set osTypesValues(value: any[]) {
    this._osTypesValues = value;
  }

  /**
   * Opzioni per la scrittura di metriche
   */
  private _writeOnEdgeOriginTypeValues!: any[];

  get writeOnEdgeOriginTypeValues(): any[] {
    return this._writeOnEdgeOriginTypeValues;
  }

  set writeOnEdgeOriginTypeValues(value: any[]) {
    this._writeOnEdgeOriginTypeValues = value;
  }

  /**
   * Numero massimo di punti telemetrici che possono essere richiesti al BE
   */
  private _maxTelemetryPointListSize!: number | null;

  get maxTelemetryPointListSize(): number | null {
    return this._maxTelemetryPointListSize;
  }

  set maxTelemetryPointListSize(value: number) {
    this._maxTelemetryPointListSize = value;
  }

  /**
   * Opzioni per l'utilizzo dei servizi di AWS
   */

  private _awsOptions!: AWSConnectionOptions | null;

  get awsOptions(): AWSConnectionOptions | null {
    return this._awsOptions;
  }

  set awsOptions(value: AWSConnectionOptions) {
    this._awsOptions = value;
  }

  /**
   * Default config delle api external application
   */

  private _eaApiConfigMapDefault!: Record<string, ApiConfig> | null;

  get eaApiConfigMapDefault(): Record<string, ApiConfig> | null {
    return this._eaApiConfigMapDefault;
  }

  set eaApiConfigMapDefault(value: Record<string, ApiConfig>) {
    this._eaApiConfigMapDefault = value;
  }


  cleanupServiceFields() {
    this._appId = null;
    this._envId = null;
    this._appVersion = null;
    this._envIdLabel = null;
    this._user = null;
    this._localeList = [];
    this._rolesList = [];
    this._modbusDataTypeValues = [];
    this._modbusFunctionValues = [];
    this._byteOrderValues = [];
    this._defaultByteOrderValue = null;
    this._protocolTypeValues = [];
    this._osTypesValues = [];
    this._maxTelemetryPointListSize = null;
    this._awsOptions = null;
  }

  /**
   * Tokenizes the appVersion returning only the first part
   */
  getAppVersion(): string {
    // Version is in the form x.y.z_buildData
    // Returning only x.y.z
    if (this.appVersion !== undefined) {
      return this.appVersion.split('_')[0];
    } else {
      return '';
    }

  }

  /**
   * The method is used to store in the Global Model Service all the params in the postLogin service response
   * @param cb the GetTransportItemDetailCB object
   */
  public storeParamsIntoGlobalModel(cb: PostLoginCB): void {
    this.appId = cb.o.appId;
    this.envId = cb.o.envId;
    this.appVersion = cb.o.appVersion;
    this.envIdLabel = cb.o.envIdLabel;

    this.localeList = cb.o.localeList;
    this.rolesList = cb.o.roles;

    this.modbusDataTypeValues = cb.o.modbusDataTypeValues;
    this.s7DataTypeValues = cb.o.s7DataTypeValues;
    this.modbusFunctionValues = cb.o.modbusFunctionValues;
    this.s7FunctionValues = cb.o.s7FunctionValues;
    this.byteOrderValues = cb.o.byteOrderValues;
    this.defaultByteOrderValue = cb.o.defaultByteOrderValue;
    this.protocolTypeValues = cb.o.protocolTypeValues;
    this.maxTelemetryPointListSize = cb.o.maxTelemetryPointListSize;

    this.osTypesValues = cb.o.osTypesValues;
    this.writeOnEdgeOriginTypeValues = cb.o.writeOnEdgeOriginTypeValues;

    this.awsOptions = cb.o.awsOptions;

    if (!!this.awsOptions) {
      this.s3.setConnectionParameters(this.s3.AWS_BUCKET, this.s3.AWS_REGION, this.awsOptions.accessKeyId,
        this.awsOptions.secretAccessKey, (this.s3.AWS_PROTOCOL === 'https'));
    }

    this.eaApiConfigMapDefault = cb.o.eaApiConfigMapDefault;

    this.user = new PostLoginUser();

    const cbUser = cb.o.user;
    if (!!cbUser) {

      this.user.id = cbUser.id;
      this.user.username = cbUser.username;
      this.user.email = cbUser.email;
      this.user.firstname = cbUser.firstname;
      this.user.lastname = cbUser.lastname;
      this.user.language = cbUser.language;
      this.user.roles = cbUser.roles;


      this.user.menuItems = new Array<MenuGroup>();
      const theMenu = cbUser.menuItems;
      if (!!theMenu) {
        theMenu.forEach(elem => {
          const innerMenuItem = new MenuGroup();
          innerMenuItem.id = elem.id;
          innerMenuItem.name = elem.name;
          innerMenuItem.roles = elem.roles;
          innerMenuItem.voices = elem.voices;
          innerMenuItem.orderIndex = elem.orderIndex;

          this.user.menuItems?.push(innerMenuItem);

        });
      }

      this.user.partition = cbUser.partition;

      this.user.pushNotificationInfo = cbUser.pushNotificationInfo;

    }

    this.setCountryFlag();

    if (!!this.user.language && this.isValidLanguage(this.user.language)) {
      this.translate.use(this.user.language);
      this.be.setLanguage(this.user.language);
      localStorage.setItem('language', this.user.language);
    }

    if (environment.production) {
      // ADD USER FOR BUGSNAG REPORTS
      Bugsnag.addOnError(event => event.setUser(this.user.id.toString(), this.user.username, `${this.user.firstname} ${this.user.lastname}`));
      Bugsnag.addOnError(event => event.addMetadata('APP', {version: this.appVersion}));
    }

  }

  public userHasAdminRole(): boolean {

    const flag = this.user.roles?.includes('R_ADMIN');

    if (flag) {
      return flag;
    }
    return false;
  }

  public userHasFullUserRole(): boolean {

    const flag = this.user.roles?.includes('R_USER_FULL');

    if (flag) {
      return flag;
    }
    return false;
  }

  /**
   * The method composes the base URL of the BE
   */
  public composeUrl(): string {
    return `${environment.APIProtocol}${environment.APIIp}:${environment.APIPort}${environment.APIContext}`;
  }

  public composeBaseUrl(): string {
    return `${environment.APIProtocol}${environment.APIIp}:${environment.APIPort}`;
  }

  public composeBaseFEUrl(): string {
    return window.location.origin;
  }

  /**
   * Set country flag basing on locale
   */
  private setCountryFlag(): void {
    for (const locale of this.localeList) {
      if (locale.locale === 'it') {
        locale.countryFlag = 'it';
      } else if (locale.locale === 'en') {
        locale.countryFlag = 'gb';
      } else if (locale.locale === 'de') {
        locale.countryFlag = 'de';
      } else if (locale.locale === 'es') {
        locale.countryFlag = 'es';
      } else if (locale.locale === 'fr') {
        locale.countryFlag = 'fr';
      }
    }
  }

  private isValidLanguage(lang: string | undefined) {
    switch (lang) {
      case 'it':
      case 'en':
      case 'de':
      case 'es':
      case 'fr':
        return true;
      default:
        return false;
    }
  }

}
